import classNames from "classnames";
import _ from "lodash";
import { useState, MouseEvent } from "react";
import { Link } from "react-router-dom";

import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  ListItemIcon,
  Typography
} from "@material-ui/core";
import {
  AccountBox,
  AccountCircle,
  AccountTree,
  Business,
  Lock,
  ListAlt,
  MeetingRoom
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

import palette from "src/styles/palette";
import { getLastUsedTimeForSubaccount } from "src/util/subaccountUsage";

import { useAuth0 } from "./AuthProvider";
import {
  LoggedInAdminSubaccountMembership,
  Tenant,
  useAdmin,
  useAdminSubaccountMemberships,
  useTenant
} from "./LoggedInAdminProvider";
import SupportWidget from "./SupportWidget";

const useStyles = makeStyles(() => ({
  menu: {
    maxWidth: "300px"
  },
  subheader: {
    lineHeight: "24px",
    "&:not(:first-child)": {
      marginTop: "12px"
    }
  },
  menuIcon: {
    minWidth: "24px"
  },
  headerLink: {
    color: "inherit",
    textDecoration: "none"
  },
  deactivatedTenant: {
    backgroundColor: palette.red
  },
  deactivatedTenantText: {
    fontStyle: "italic"
  }
}));

enum SubaccountDisplayMode {
  NONE,
  ALL,
  RECENT
}

const MAX_SUBACCOUNTS_FOR_LIST = 5;

function getSubaccountDisplay(
  memberships: LoggedInAdminSubaccountMembership[],
  tenant: Tenant
): {
  displayMode: SubaccountDisplayMode;
  subaccountOptions: { id: string; label: string }[];
} {
  if (!tenant.isUsingSubaccounts) {
    return {
      displayMode: SubaccountDisplayMode.NONE,
      subaccountOptions: []
    };
  }

  if (memberships.length < 2) {
    return {
      displayMode: SubaccountDisplayMode.NONE,
      subaccountOptions: []
    };
  }

  if (memberships.length <= MAX_SUBACCOUNTS_FOR_LIST) {
    return {
      displayMode: SubaccountDisplayMode.ALL,
      subaccountOptions: _.sortBy(memberships, m => m.subaccount.name).map(
        m => ({ id: m.subaccount.id, label: m.subaccount.name })
      )
    };
  }

  return {
    displayMode: SubaccountDisplayMode.RECENT,
    subaccountOptions: _.sortBy(
      memberships.filter(
        m => getLastUsedTimeForSubaccount(m.subaccount.id) != null
      ),
      m => -getLastUsedTimeForSubaccount(m.subaccount.id)!,
      m => m.subaccount.name
    )
      .slice(0, MAX_SUBACCOUNTS_FOR_LIST)
      .map(m => ({ id: m.subaccount.id, label: m.subaccount.name }))
  };
}

export default function TopBar({
  title,
  titleLink
}: {
  title: string;
  titleLink: string;
}): JSX.Element {
  const auth0 = useAuth0();
  const admin = useAdmin()!;
  const memberships = useAdminSubaccountMemberships()!;
  const tenant = useTenant()!;
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const classes = useStyles();

  const { displayMode, subaccountOptions } = getSubaccountDisplay(
    memberships,
    tenant
  );

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseUserMenu = () => {
    setMenuOpen(false);
  };

  const handleLogout = () =>
    auth0.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`
      }
    });
  const deactivatedOrOnHold = !!tenant.deactivatedAt || !!tenant.onHold;
  return (
    <AppBar
      elevation={0}
      className={classNames({
        [classes.deactivatedTenant]: deactivatedOrOnHold
      })}
    >
      <Toolbar>
        <h1 style={{ flexGrow: 1 }}>
          <Link to={titleLink} className={classes.headerLink}>
            {title}
          </Link>
          {deactivatedOrOnHold ? (
            <span className={classes.deactivatedTenantText}>
              {tenant.deactivatedAt ? <> (deactivated tenant)</> : null}
              {tenant.onHold ? <> (calling disabled)</> : null}
            </span>
          ) : null}
        </h1>
        {auth0.isAuthenticated ? (
          <div>
            <SupportWidget />
            <IconButton onClick={handleOpenUserMenu} color="inherit">
              <AccountCircle />
            </IconButton>
            <Menu
              id="user-menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                horizontal: "center",
                vertical: "bottom"
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "center"
              }}
              open={menuOpen}
              onClick={handleCloseUserMenu}
              onClose={handleCloseUserMenu}
              MenuListProps={{ className: classes.menu }}
            >
              <ListSubheader className={classes.subheader}>
                {admin.fullName}
              </ListSubheader>
              <MenuItem dense component={Link} to="/user/profile">
                <ListItemIcon className={classes.menuIcon}>
                  <AccountBox fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit" noWrap>
                  My Profile
                </Typography>
              </MenuItem>
              {admin.isTenantOwner && (
                <MenuItem dense component={Link} to="/organization/settings">
                  <ListItemIcon className={classes.menuIcon}>
                    <Business fontSize="small" />
                  </ListItemIcon>

                  <Typography variant="inherit" noWrap>
                    Organization Settings
                  </Typography>
                </MenuItem>
              )}
              {admin.isStaff && (
                <MenuItem dense component={Link} to="/staff">
                  <ListItemIcon className={classes.menuIcon}>
                    <Lock fontSize="small" />
                  </ListItemIcon>

                  <Typography variant="inherit" noWrap>
                    STW Staff Admin
                  </Typography>
                </MenuItem>
              )}
              <MenuItem dense onClick={handleLogout}>
                <ListItemIcon className={classes.menuIcon}>
                  <MeetingRoom fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit" noWrap>
                  Logout
                </Typography>
              </MenuItem>

              {displayMode !== SubaccountDisplayMode.NONE && (
                <>
                  <ListSubheader className={classes.subheader}>
                    {displayMode === SubaccountDisplayMode.RECENT
                      ? "Recent Subaccounts"
                      : "Subaccounts"}
                  </ListSubheader>
                  {subaccountOptions.map(({ id, label }) => (
                    <MenuItem
                      dense
                      component={Link}
                      to={`/subaccounts/${id}/campaigns`}
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <AccountTree fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit" noWrap>
                        {label}
                      </Typography>
                    </MenuItem>
                  ))}
                  {displayMode === SubaccountDisplayMode.RECENT && (
                    <MenuItem dense component={Link} to="/user/subaccounts">
                      <ListItemIcon className={classes.menuIcon}>
                        <ListAlt fontSize="small" />
                      </ListItemIcon>

                      <Typography variant="inherit" noWrap>
                        All Subaccounts...
                      </Typography>
                    </MenuItem>
                  )}
                </>
              )}
            </Menu>
          </div>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}
